import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles
    .filter((a) => a.id !== id && a.category === article?.category)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">
              文章未找到
            </h2>
            <Link
              to="/"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.image,
            publisher: {
              '@type': 'Organization',
              name: '国产第一页',
            },
          }),
        }}
      />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '国货文章' },
              { label: article.title },
            ]}
          />
        </div>

        <article className="pb-14 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-2 mt-4">
                <span className="text-xs text-foreground-400">作者：{article.author}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 bg-secondary-100 text-secondary-800 text-xs rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/80"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-bold text-foreground-950 mb-2 line-clamp-2">
                        {rel.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{rel.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}